import type { ContextWithId } from "@shared/lib/context";
import { createContext } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { z } from "zod";

export interface SignInFields {
	email: string;
	password: string;
}

export interface SignUpFields extends SignInFields {
	confirmPassword: string;
}

export const signInSchema = z.object({
	email: z.string({ error: "Email field is required" }).email("Email is not valid"),
	password: z
		.string({ error: "Password field is required" })
		.min(5, { error: "Password should contain more than 5 symbols" })
});

export const signUpSchema = z
	.object({
		email: z.string({ error: "Email field is required" }).email("Email is not valid"),
		password: z
			.string({ error: "Password field is required" })
			.min(5, { error: "Password should contain more than 5 symbols" }),
		confirmPassword: z.string({ error: "Confirm password field is required" })
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "Passwords don't match",
		path: ["confirmPassword"]
	});

export type ISignInSchema = z.infer<typeof signInSchema>;
export type ISignUpSchema = z.infer<typeof signUpSchema>;

export interface IAuthFormContext<FormFields extends FieldValues> {
	formApi: UseFormReturn<FormFields>;
}

const AuthContext = createContext<IAuthFormContext<SignInFields> | null>(null);
export const AuthFormContext: ContextWithId<IAuthFormContext<SignInFields> | null> = {
	Id: "AuthFormContext",
	Context: AuthContext
};

const SignUpContext = createContext<IAuthFormContext<SignUpFields> | null>(null);
export const SignUpFormContext: ContextWithId<IAuthFormContext<SignUpFields> | null> = {
	Id: "SignUpFormContext",
	Context: SignUpContext
};
